import { z } from 'zod';
const emailSchema = z.string().email();
export const QBO_BILL_EMAIL_MAX_LENGTH = 100;
export const QBO_BILL_EMAIL_CC_MAX_LENGTH = 200;
export function parseEmailList(raw) {
    if (!raw)
        return [];
    return raw
        .split(/[;,]/)
        .map((e) => e.trim())
        .filter(Boolean);
}
export function isValidEmailAddress(part) {
    return part.length <= 254 && emailSchema.safeParse(part).success;
}
export function uniqueEmailAddresses(addresses) {
    const seen = new Set();
    return addresses.filter((address) => {
        const key = address.toLowerCase();
        if (seen.has(key))
            return false;
        seen.add(key);
        return true;
    });
}
export function classifyEmailRecipients(raw) {
    const candidates = [];
    const invalid = [];
    const seen = new Set();
    for (const [index, value] of parseEmailList(raw).entries()) {
        if (!isValidEmailAddress(value)) {
            invalid.push({ value, reason: 'invalid_format', index });
            continue;
        }
        const dedupeKey = value.toLowerCase();
        if (seen.has(dedupeKey)) {
            invalid.push({ value, reason: 'duplicate', index });
            continue;
        }
        seen.add(dedupeKey);
        candidates.push({ value, index });
    }
    const primary = candidates.find(({ value }) => value.length <= QBO_BILL_EMAIL_MAX_LENGTH);
    if (!primary) {
        for (const candidate of candidates) {
            invalid.push({
                value: candidate.value,
                reason: 'primary_too_long',
                index: candidate.index,
            });
        }
        return {
            valid: [],
            invalid: invalid
                .sort((a, b) => a.index - b.index)
                .map(({ value, reason }) => ({ value, reason })),
            primary: null,
            cc: [],
        };
    }
    const cc = [];
    for (const candidate of candidates) {
        if (candidate === primary)
            continue;
        if ([...cc, candidate.value].join(',').length > QBO_BILL_EMAIL_CC_MAX_LENGTH) {
            invalid.push({
                value: candidate.value,
                reason: 'cc_limit_exceeded',
                index: candidate.index,
            });
            continue;
        }
        cc.push(candidate.value);
    }
    return {
        valid: [primary.value, ...cc],
        invalid: invalid
            .sort((a, b) => a.index - b.index)
            .map(({ value, reason }) => ({ value, reason })),
        primary: primary.value,
        cc,
    };
}
export function resolvePayorEmail(raw) {
    return classifyEmailRecipients(raw).primary;
}
export const BILL_EMAIL_STATES = ['usable', 'missing', 'invalid', 'unknown'];
export function billEmailState(mirror) {
    return mirror === null ? 'unknown' : mirrorBillEmailState(mirror);
}
export function mirrorBillEmailState(mirror) {
    if (parseEmailList(mirror.billEmail).length === 0)
        return 'missing';
    return classifyEmailRecipients(mirror.billEmail).primary === null ? 'invalid' : 'usable';
}
export function resolveNoticeEmail(contacts) {
    for (const contact of contacts) {
        for (const value of parseEmailList(contact.value)) {
            if (isValidEmailAddress(value))
                return value;
        }
    }
    return null;
}
export function formatIgnoredEmailRecipients(parts) {
    if (parts.length === 0)
        return undefined;
    const labels = parts.map(({ value, reason }) => {
        switch (reason) {
            case 'invalid_format':
                return `${value} (invalid address)`;
            case 'duplicate':
                return `${value} (duplicate address)`;
            case 'primary_too_long':
                return `${value} (exceeds QuickBooks' 100-character primary limit)`;
            case 'cc_limit_exceeded':
                return `${value} (exceeds QuickBooks' 200-character CC limit)`;
            default: {
                const _exhaustive = reason;
                throw new Error(`Unhandled ignored email reason: ${_exhaustive}`);
            }
        }
    });
    return `Ignored invoice recipient${parts.length === 1 ? '' : 's'}: ${labels.join('; ')}`;
}
//# sourceMappingURL=email.js.map